/**
 * ALAP (backward) resolution for a single scheduling node — mirror of `asap.ts`.
 *
 * Pure function. Algorithm per `04-` §16 backward pass:
 *   1. Initial candidate = node.anchorEndTs (preferred-date mode, no successors).
 *   2. For each successor edge: candidate = applyEdgeBackward(succStart, edge).
 *      latestEnd = min(initial, min over all successors).
 *   3. Apply hard-constraint upper bound (SNLT/MSO/FNLT/MFO).
 *   4. latestStart = node.calendar.addDuration(latestEnd, -duration, durationUnit).
 *
 * `reason` reflects what drove the date:
 *   - some successor edge pulled latestEnd in   → `{kind:"alap", linkId, targetId}`
 *   - hard-constraint upper bound pulled it in  → `{kind:"constraint"}`
 *   - neither (anchor stays)                    → `{kind:"preferred"}`
 *
 * Preference rule (same priority as asap.ts): constraint > successor > preferred.
 */

import type { ScheduleReason, NormalizedTaskID } from "../types";
import type { SchedNode, SchedEdge } from "../graph/types";
import {
  applyEdgeBackward,
  applyConstraintUpperBound,
  applyConstraintLowerBound
} from "../analysis/slack";

export interface AlapInputs {
  readonly node: SchedNode;
  readonly successorEdges: readonly SchedEdge[];
  /** Map of already-planned successor start timestamps (epoch ms). */
  readonly succStarts: ReadonlyMap<NormalizedTaskID, number>;
  readonly applyConstraints: boolean;
  /**
   * Explicit project end horizon (`gantt.config.project_end`, per D-23 /
   * `04-` §17). For a node with no successor edges this acts as a CEILING on
   * `latestEnd` — applied as a `min` after the hard-constraint upper bound, so
   * it both supplies the terminal-node latestEnd (no constraint case) and
   * overrides a constraint that would otherwise finish the task past the
   * horizon (the backward mirror of asap.ts's project-start floor; GS-1771).
   * Without this, ALAP scheduling falls back to `node.anchorEndTs` and ignores
   * `gantt.config.project_end` entirely.
   */
  readonly projectEndTs?: number;
  /**
   * `SchedulingConfig.gapBehavior` — controls how leaves with both an
   * anchor and successors resolve. `"preserve"` (default) keeps the
   * leaf at `min(anchor, succStart-derived)`. `"compress"` ignores the
   * anchor and uses the successor-derived value directly, pulling tasks
   * to their latest feasible end. Currently alap already initializes
   * `latestEndTs = POSITIVE_INFINITY` so it never anchors as a floor —
   * the parameter is accepted for symmetry with asap and documented for
   * the future case where preserve semantics need anchor-based clamping.
   */
  readonly gapBehavior?: "preserve" | "compress";
}

export interface AlapResult {
  readonly startTs: number;
  readonly endTs: number;
  readonly reason: ScheduleReason;
}

export function resolveAlap(inputs: AlapInputs): AlapResult {
  const { node, successorEdges, succStarts, projectEndTs } = inputs;

  // Start with no upper bound — successors / hard constraints impose bounds.
  // This matches v1's `alap_strategy.ts` semantics where successors override
  // the task's own end_date (anchor) when they exist; anchor is the
  // FALLBACK when neither successors nor a hard upper-bound constraint exist.
  let latestEndTs = Number.POSITIVE_INFINITY;
  let drivingEdge: SchedEdge | undefined;

  for (const e of successorEdges) {
    const succStart = succStarts.get(e.target);
    if (succStart === undefined) continue;
    const candidate = applyEdgeBackward(succStart, e);
    if (candidate < latestEndTs) {
      latestEndTs = candidate;
      drivingEdge = e;
    }
  }

  // Snap to working time — same rationale as asap.ts. v1 uses
  // `getClosestWorkTime({date, dir: "past", ...})` before storing.
  if (drivingEdge !== undefined && isFinite(latestEndTs)) {
    latestEndTs = node.calendar
      .snapToWorking(new Date(latestEndTs), "backward")
      .getTime();
  }

  const beforeConstraint = latestEndTs;
  latestEndTs = applyConstraintUpperBound(latestEndTs, node);
  const constraintDrove = latestEndTs !== beforeConstraint;

  // Project-end ceiling (per D-23 — the backward mirror of asap.ts's
  // project-start floor; GS-1771). A node with no successor must not finish
  // after an explicitly-configured `gantt.config.project_end`. Applied AFTER
  // the hard-constraint upper bound and as a `min`, so:
  //   - a no-constraint terminal node lands exactly on project_end (this also
  //     subsumes the old `!isFinite` → projectEnd fallback, since
  //     `min(+Infinity, projectEnd) === projectEnd`);
  //   - an FNLT/SNLT that the backward drag handler installs at a dragged
  //     past-project_end date is overridden DOWN to project_end. This is the
  //     bug: the forward path already snapped a task dragged before
  //     project_start UP to project_start (via the SNET-overriding floor in
  //     asap.ts), but backward left a task dragged past project_end beyond the
  //     horizon because the FNLT made `latestEndTs` finite and skipped the
  //     fallback;
  //   - a tighter constraint that finishes BEFORE project_end is respected
  //     (`min` keeps the earlier date).
  // A node bounded by a successor edge is positioned by that successor and
  // left alone, mirroring asap.ts's `!hasPreds` gate on its floor.
  const hasSuccessors = successorEdges.length > 0;
  if (!hasSuccessors && projectEndTs !== undefined && Number.isFinite(projectEndTs)) {
    latestEndTs = Math.min(latestEndTs, projectEndTs);
  }

  // Fallback when no upper bound was supplied at all (terminal node with no own
  // constraint AND no configured project_end — the ceiling above already
  // covers the configured case). The anchor is the tertiary fallback for the
  // rare snapshot with no project horizon (e.g., test fixtures that build
  // snapshots without project_start/project_end). A node WITH successors whose
  // starts were all unresolved also lands here. v1's `alap_strategy.ts:67-71`
  // matches this precedence.
  if (!isFinite(latestEndTs)) {
    if (projectEndTs !== undefined && Number.isFinite(projectEndTs)) {
      latestEndTs = projectEndTs;
    } else {
      latestEndTs = node.anchorEndTs;
    }
  }

  // Compute start. For SUMMARIES: the stored `duration` field on a project
  // task doesn't track the actual child-derived span — `schedule_pass.ts`
  // hands the resolver an effective node whose `anchor{Start,End}Ts` is the
  // children-derived bounds, so `anchorEndTs - anchorStartTs` is the right
  // span. (Symmetric with asap.ts which already uses the anchor span for
  // summaries — without this, a summary alap-shifted to project_end ended
  // up with `latestEnd − node.duration`, dragging the children block by the
  // stale stored duration. Test 054 step 1: project 13 had stored duration
  // 10d but children spanned 6 working days, so the summary's start landed
  // at `01-05 − 10 = ~21-04`, instead of `01-05 − 6 = 25-04`, producing a
  // non-zero translation delta and pulling the children out of position.)
  let startTs;
  if (node.kind === "summary") {
    const span = node.anchorEndTs - node.anchorStartTs;
    startTs = latestEndTs - span;
  } else {
    startTs = node.duration === 0
      ? latestEndTs
      : node.calendar
          .addDuration(new Date(latestEndTs), -node.duration, node.durationUnit)
          .getTime();
  }

  // Phase D Day 2 fix: SNET / FNET / MSO / MFO lower bounds apply in BOTH
  // directions. The task can't start before SNET regardless of whether
  // we're scheduling forward or backward — SNET is a lower bound on start.
  // If the lower bound pushes start up, push end up correspondingly.
  const beforeLowerBound = startTs;
  startTs = applyConstraintLowerBound(startTs, node);
  if (startTs !== beforeLowerBound) {
    // Lower bound pushed start; recompute end = start + duration.
    if (node.kind === "summary") {
      const span = node.anchorEndTs - node.anchorStartTs;
      latestEndTs = startTs + span;
    } else {
      latestEndTs = node.duration === 0
        ? startTs
        : node.calendar.addDuration(new Date(startTs), node.duration, node.durationUnit).getTime();
    }
  }

  let reason: ScheduleReason;
  if (constraintDrove) {
    reason = { kind: "constraint" };
  } else if (drivingEdge) {
    reason = { kind: "alap", linkId: drivingEdge.linkId, targetId: drivingEdge.target };
  } else {
    reason = { kind: "preferred" };
  }

  return { startTs, endTs: latestEndTs, reason };
}
